import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { useColumnResize } from "@/hooks/useColumnResize";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { SourceIcon } from "@/components/SourceIcon";
import { useAuth } from "@/context/AuthContext";
import { useSuspendedAlert } from "@/context/SuspendedClientAlert";
import { toast } from "sonner";
import { PlusIcon as Plus, MagnifyingGlassIcon as MagnifyingGlass, TicketIcon as TicketIcon, EnvelopeSimpleIcon as EnvelopeSimple, UserCircleIcon as UserCircle, PulseIcon as Pulse, CircleIcon as Circle, TrashIcon as Trash, FunnelIcon as Funnel, ArrowsClockwiseIcon as ArrowsClockwise, WarningIcon as Warning, ListBulletsIcon as ListBullets, TableIcon as TableIco, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown, CheckIcon as Check } from "@phosphor-icons/react";

export const Badge = ({ item, fallback = "—" }) => item ? (
  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium border"
    style={{ backgroundColor: (item.colore || "#94a3b8") + "20", color: item.colore || "#64748b", borderColor: (item.colore || "#94a3b8") + "55" }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.colore || "#94a3b8" }} />{item.nome}
  </span>
) : <span className="text-xs text-muted-foreground">{fallback}</span>;

const AV_COLORS = ["#fca5a5", "#fcd34d", "#86efac", "#93c5fd", "#c4b5fd", "#f9a8d4", "#67e8f9", "#fdba74", "#a5b4fc", "#5eead4"];
export const avatarColor = (s) => AV_COLORS[[...String(s || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AV_COLORS.length];
export const initial = (s) => String(s || "?").trim().charAt(0).toUpperCase() || "?";

export const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso); const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "pochi secondi fa";
  const m = Math.floor(s / 60); if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60); if (h < 24) return `${h} ${h === 1 ? "ora" : "ore"} fa`;
  const g = Math.floor(h / 24); if (g < 30) return `${g} ${g === 1 ? "giorno" : "giorni"} fa`;
  const mo = Math.floor(g / 30); return `${mo} ${mo === 1 ? "mese" : "mesi"} fa`;
};

const SLA_BADGE = {
  active: { label: "In tempo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  first_response_due_soon: { label: "1ª risposta in scadenza", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  resolution_due_soon: { label: "In scadenza", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  first_response_breached: { label: "1ª risposta scaduta", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  resolution_breached: { label: "Scaduto", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  paused: { label: "Sospeso", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  met: { label: "Rispettato", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  closed_without_sla: { label: "Senza SLA", cls: "bg-slate-50 text-slate-400 border-slate-200" },
  none: null,
};
export const SlaBadge = ({ status, testid }) => {
  const s = SLA_BADGE[status];
  if (!s) return null;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium border ${s.cls}`} data-testid={testid}>{s.label}</span>;
};

// Cella "Assegnato a" con popup a due schede (Gruppo / Agente), ricerca e checkmark.
const stopEvt = (e) => e.stopPropagation();
const AssigneeCell = ({ t, meta, patchRow }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("agente");
  const groups = useMemo(() => [...(meta.groups || [])].sort((a, b) => (a.nome || "").localeCompare(b.nome || "")), [meta.groups]);
  const users = useMemo(() => [...(meta.users || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")), [meta.users]);
  const groupName = t.gruppo?.nome;
  const agentName = t.assegnatario_nome;
  const curGroup = t.gruppo_id || t.gruppo?.id || null;
  const label = [groupName, agentName].filter(Boolean).join(" / ");
  const chooseGroup = (id) => { patchRow(t.id, "gruppo_id", id); setTab("agente"); };
  const chooseAgent = (id) => { patchRow(t.id, "assegnato_a_user_id", id); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={stopEvt}
          className="inline-flex items-center gap-2 h-7 px-1.5 rounded-sm hover:bg-secondary max-w-full text-left transition-colors"
          data-testid={`row-assegnatario-${t.id}`}>
          <UserCircle size={16} className="text-muted-foreground shrink-0" />
          <span className="text-sm whitespace-nowrap">{label || <span className="text-amber-600">Non assegnato</span>}</span>
          <CaretDown size={12} className="text-muted-foreground shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} onClick={stopEvt}
        className="w-72 p-0 rounded-sm overflow-hidden" data-testid={`assignee-popover-${t.id}`}>
        <div className="grid grid-cols-2">
          <button type="button" onClick={() => setTab("gruppo")}
            className={`px-3 py-2 text-left border-b-2 transition-colors ${tab === "gruppo" ? "border-amber-500 bg-amber-50" : "border-transparent hover:bg-secondary/20"}`}
            data-testid={`assignee-tab-gruppo-${t.id}`}>
            <div className={`text-[10px] font-semibold uppercase tracking-wider ${tab === "gruppo" ? "text-amber-700" : "text-muted-foreground"}`}>Gruppo</div>
            <div className="text-sm truncate text-slate-700">{groupName || "—"}</div>
          </button>
          <button type="button" onClick={() => setTab("agente")}
            className={`px-3 py-2 text-left border-b-2 transition-colors ${tab === "agente" ? "border-blue-500 bg-blue-50" : "border-transparent hover:bg-secondary/20"}`}
            data-testid={`assignee-tab-agente-${t.id}`}>
            <div className={`text-[10px] font-semibold uppercase tracking-wider ${tab === "agente" ? "text-blue-700" : "text-muted-foreground"}`}>Agente</div>
            <div className="text-sm truncate text-slate-700">{agentName || "—"}</div>
          </button>
        </div>
        <Command key={tab} className={tab === "gruppo" ? "bg-amber-50/40" : "bg-blue-50/40"}>
          <CommandInput placeholder={tab === "gruppo" ? "Cerca gruppo" : "Cerca agente"} data-testid={`assignee-search-${t.id}`} />
          <CommandList className="max-h-64">
            <CommandEmpty>Nessun risultato</CommandEmpty>
            {tab === "gruppo" ? (
              <CommandGroup>
                {groups.map((g) => (
                  <CommandItem key={g.id} value={g.nome} onSelect={() => chooseGroup(g.id)} data-testid={`assignee-group-opt-${g.id}`}>
                    <span className="flex-1 truncate">{g.nome}</span>
                    {curGroup === g.id && <Check size={15} weight="bold" className="text-amber-600 shrink-0" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              <CommandGroup>
                <CommandItem value="Non assegnato" onSelect={() => chooseAgent(null)} data-testid={`assignee-agent-none-${t.id}`}>
                  <span className="flex-1 truncate text-muted-foreground">Non assegnato</span>
                  {!t.assegnato_a_user_id && <Check size={15} weight="bold" className="text-blue-600 shrink-0" />}
                </CommandItem>
                {users.map((u) => (
                  <CommandItem key={u.id} value={u.name} onSelect={() => chooseAgent(u.id)} data-testid={`assignee-agent-opt-${u.id}`}>
                    <span className="flex-1 truncate">{u.name}</span>
                    {t.assegnato_a_user_id === u.id && <Check size={15} weight="bold" className="text-blue-600 shrink-0" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};


const EMPTY_FILTERS = {
  stato_id: "", priorita_id: "", sorgente_id: "", gruppo_id: "", assegnato_a_user_id: "",
  cliente_id: "", sla_status: "", sla_policy_id: "", visibile_area_cliente: "", quick: "",
  data_da: "", data_a: "", mostra_finali: false,
};
const countActive = (f) => Object.entries(f).filter(([k, v]) => k !== "mostra_finali" && v).length + (f.mostra_finali ? 1 : 0);

const PREFS_KEY = "helpdesk_ticket_list_preferences";
const SLA_ORD = { resolution_breached: 0, first_response_breached: 1, resolution_due_soon: 2, first_response_due_soon: 3, active: 4, paused: 5, met: 6, closed_without_sla: 7, none: 8 };
const sortVal = (t, key) => {
  switch (key) {
    case "numero_ticket": return t.numero_ticket || "";
    case "oggetto": return (t.oggetto || "").toLowerCase();
    case "stato": return (t.stato?.nome || "").toLowerCase();
    case "priorita": return t.priorita?.ordine ?? 999;
    case "cliente": return (t.cliente_nome || "").toLowerCase();
    case "richiedente": return (t.richiedente_nome || t.richiedente_email || "").toLowerCase();
    case "assegnatario": return (t.assegnatario_nome || "\uffff").toLowerCase();
    case "sla": return SLA_ORD[t.sla_status] ?? 8;
    case "data_creazione": return t.data_creazione || "";
    default: return t.data_ultimo_aggiornamento || "";
  }
};
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; } };

export default function TicketList() {
  const nav = useNavigate();
  const { user: authUser } = useAuth();
  const { alertSuspended } = useSuspendedAlert();
  const prefs0 = loadPrefs();
  const _urlQS = new URLSearchParams(window.location.search);
  const _urlStato = _urlQS.get("stato_id");
  const _urlSla = _urlQS.get("sla_status");
  const initFilters = { ...EMPTY_FILTERS, ...(prefs0.f || {}),
    ...(_urlSla ? { sla_status: _urlSla } : {}),
    ...(_urlStato ? { stato_id: _urlStato, mostra_finali: true } : {}) };
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({ statuses: [], priorities: [], sources: [], groups: [], users: [], clients: [], settings: {}, slaPolicies: [] });
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clientQuery, setClientQuery] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);
  const [refOptions, setRefOptions] = useState([]);
  const [refFocus, setRefFocus] = useState(false);
  const [freeNames, setFreeNames] = useState([]);
  const [q, setQ] = useState(prefs0.q || "");
  const [f, setF] = useState(initFilters);      // filtri applicati
  const [fd, setFd] = useState(initFilters);     // bozza filtri (nel pannello)
  const [sortBy, setSortBy] = useState(prefs0.sortBy || "data_ultimo_aggiornamento");
  const [sortDir, setSortDir] = useState(prefs0.sortDir || "desc");
  const [viewMode, setViewMode] = useState(prefs0.viewMode || "list");
  const [filterOpen, setFilterOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const pageSize = 50;

  const load = () => {
    const params = { q, page, page_size: pageSize };
    Object.entries(f).forEach(([k, v]) => { if (k === "mostra_finali") return; if (v) params[k] = v; });
    if (!f.mostra_finali) params.nascondi_finali = "true";
    api.get("/tickets", { params }).then(({ data }) => {
      const its = data.items || data;
      setItems(its); setTotal(data.total ?? its.length); setPages(data.pages ?? 1); setLastRefresh(new Date());
    }).catch(() => setItems([]));
  };
  useEffect(() => {
    Promise.all([
      api.get("/ticket-statuses"), api.get("/ticket-priorities"), api.get("/ticket-sources"),
      api.get("/ticket-groups"), api.get("/tecnici"), api.get("/clients"),
      api.get("/ticket-settings"), api.get("/hd-sla-policies"),
    ]).then(([st, pr, src, grp, usr, cli, s, sla]) => setMeta({
      statuses: st.data, priorities: pr.data, sources: src.data, groups: grp.data,
      users: [...usr.data].sort((a, b) => (a.name || "").localeCompare(b.name || "")), clients: cli.data, settings: s.data, slaPolicies: sla.data,
    })).catch(() => {});
  }, []);
  // Pulisci la query string dall'URL dopo averla applicata ai filtri iniziali
  useEffect(() => {
    if (window.location.search) window.history.replaceState({}, "", window.location.pathname);
  }, []);
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [q, f]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, f, page]);

  // Auto-refresh ogni 30s, sospeso durante azioni (dialog aperti, selezione bulk)
  const busy = showNew || !!delTarget || selected.length > 0 || filterOpen;
  const busyRef = useRef(busy); busyRef.current = busy;
  const filtersRef = useRef({ q, f }); filtersRef.current = { q, f };
  const pageRef = useRef(page); pageRef.current = page;
  useEffect(() => {
    const iv = setInterval(() => {
      if (busyRef.current) return;
      const { q: cq, f: cf } = filtersRef.current;
      const params = { q: cq, page: pageRef.current, page_size: pageSize };
      Object.entries(cf).forEach(([k, v]) => { if (k === "mostra_finali") return; if (v) params[k] = v; });
      if (!cf.mostra_finali) params.nascondi_finali = "true";
      api.get("/tickets", { params }).then(({ data }) => {
        const its = data.items || data;
        setItems(its); setTotal(data.total ?? its.length); setPages(data.pages ?? 1); setLastRefresh(new Date());
      }).catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, []);

  const filtered = useMemo(() => {
    const arr = [...(items || [])];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => { const va = sortVal(a, sortBy), vb = sortVal(b, sortBy); if (va < vb) return -1 * dir; if (va > vb) return 1 * dir; return 0; });
    return arr;
  }, [items, sortBy, sortDir]);
  const activeCount = countActive(f);

  // Vista tabella: colonne ridimensionabili + ordinamento per colonna
  const DW_TK = { check: 44, sorgente: 48, cliente: 220, numero: 90, oggetto: 300, priorita: 130, stato: 140, assegnatario: 190, creato: 150, aggiornato: 130, sla: 140, azioni: 56 };
  const { colW, startResize, totalW } = useColumnResize("helpdesk_table_col_w", DW_TK);
  const toggleSort = (k) => { if (sortBy === k) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortBy(k); setSortDir("asc"); } };
  const RH = ({ k }) => <span onMouseDown={(e) => startResize(e, k)} onClick={(e) => e.stopPropagation()} className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60" />;
  const SortHead = ({ label, k, sk }) => {
    const key = sk || k;
    return (
      <TableHead style={{ width: colW[k] }} className="relative cursor-pointer select-none hover:text-primary" onClick={() => toggleSort(key)} data-testid={`sort-${key}`}>
        <span className="inline-flex items-center gap-1">{label}{sortBy === key && (sortDir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)}</span>
        <RH k={k} />
      </TableHead>
    );
  };

  // Persistenza preferenze per utente (localStorage)
  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ q, f, sortBy, sortDir, viewMode })); } catch { /* noop */ }
  }, [q, f, sortBy, sortDir, viewMode]);

  const patchRow = async (id, field, value) => {
    try { await api.put(`/tickets/${id}`, { [field]: value }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const openFilters = () => { setFd(f); setFilterOpen(true); };
  const applyFilters = () => { setF(fd); setFilterOpen(false); };
  const resetFilters = () => { setFd(EMPTY_FILTERS); setF(EMPTY_FILTERS); setQ(""); setSortBy("data_ultimo_aggiornamento"); setSortDir("desc"); setFilterOpen(false); };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/tickets/${delTarget.id}`);
      toast.success(`Ticket #${delTarget.numero_ticket} eliminato`);
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore durante l'eliminazione"); }
    finally { setDeleting(false); }
  };

  const bulkAction = async (action, value) => {
    if (!selected.length) return;
    if (action === "delete" && !window.confirm(`Eliminare ${selected.length} ticket selezionati?`)) return;
    try {
      const { data } = await api.post("/tickets/bulk", { ids: selected, action, value });
      toast.success(`${data.aggiornati} ticket aggiornati${data.saltati ? `, ${data.saltati} saltati` : ""}`);
      setSelected([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };
  const allVisibleSelected = filtered.length > 0 && filtered.every((t) => selected.includes(t.id));
  const toggleSelectAll = () => setSelected(allVisibleSelected ? [] : filtered.map((t) => t.id));

  const openNew = async () => {
    const tel = (meta.sources || []).find((s) => s.nome === "Telefono");
    setForm({ oggetto: "", descrizione: "", cliente_id: "", cliente_nome_libero: "", richiedente_nome: "", richiedente_email: "", richiedente_telefono: "",
      priorita_id: meta.settings.default_priority_id || "", sorgente_id: (tel && tel.id) || meta.settings.default_source_id || "",
      gruppo_id: "", assegnato_a_user_id: "" });
    setRefOptions([]); setClientQuery(""); setRefFocus(false);
    setShowNew(true);
    try { const { data } = await api.get("/tickets/free-client-names"); setFreeNames(Array.isArray(data) ? data : []); } catch { setFreeNames([]); }
  };
  const selectFreeName = (name) => {
    setForm((f) => ({ ...f, cliente_id: "", cliente_nome_libero: name }));
    setRefOptions([]);
    setClientPickerOpen(false);
    setClientQuery("");
  };
  const onSelectClient = async (cid) => {
    // Selezione cliente NON compila automaticamente email/telefono: carica solo i referenti per l'autocomplete.
    setForm((f) => ({ ...f, cliente_id: cid, cliente_nome_libero: "" }));
    setClientPickerOpen(false);
    if (!cid) { setRefOptions([]); return; }
    const c = (meta.clients || []).find((x) => x.id === cid);
    if (c) alertSuspended(c);
    try {
      const { data } = await api.get(`/clients/${cid}`);
      setRefOptions(data.referenti || []);
    } catch { setRefOptions([]); }
  };
  const useFreeClientName = () => {
    const name = (clientQuery || "").trim();
    if (!name) { toast.error("Inserisci un nome cliente"); return; }
    setForm((f) => ({ ...f, cliente_id: "", cliente_nome_libero: name }));
    setRefOptions([]);
    setClientPickerOpen(false);
    setClientQuery("");
  };
  const createClientInline = async () => {
    const rag = (clientQuery || "").trim();
    if (!rag) { toast.error("Inserisci la ragione sociale del cliente"); return; }
    setCreatingClient(true);
    try {
      const { data } = await api.post("/clients", { ragione_sociale: rag });
      setMeta((m) => ({ ...m, clients: [data, ...m.clients] }));
      await onSelectClient(data.id);
      setClientQuery("");
      toast.success(`Cliente "${data.ragione_sociale}" creato`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore creazione cliente"); }
    finally { setCreatingClient(false); }
  };
  const pickReferente = (r) => {
    // Propone i dati del referente selezionato, ma restano modificabili.
    setForm((f) => ({ ...f, richiedente_nome: r.nome || "", richiedente_email: r.email || "", richiedente_telefono: r.telefono || "" }));
    setRefFocus(false);
  };
  const refSuggestions = (() => {
    const q = (form?.richiedente_nome || "").toLowerCase().trim();
    let list = refOptions;
    if (q) list = refOptions.filter((r) => `${r.nome || ""} ${r.email || ""} ${r.ruolo || ""}`.toLowerCase().includes(q));
    return list.slice(0, 6);
  })();
  const create = async () => {
    if (!form.cliente_id && !(form.cliente_nome_libero || "").trim()) { toast.error("Seleziona o inserisci un cliente"); return; }
    if (!form.richiedente_nome.trim()) { toast.error("Referente obbligatorio"); return; }
    if (!form.oggetto.trim()) { toast.error("Oggetto richiesta obbligatorio"); return; }
    if (!form.descrizione.trim()) { toast.error("Richiesta obbligatoria"); return; }
    if (!form.gruppo_id) { toast.error("Assegnazione gruppo obbligatoria"); return; }
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      if (payload.cliente_nome_libero) { payload.cliente_nome = payload.cliente_nome_libero; delete payload.cliente_id; }
      delete payload.cliente_nome_libero;
      if (!payload.richiedente_nome && !payload.richiedente_email) payload.richiedente_nome = authUser?.name || "Operatore";
      const { data } = await api.post("/tickets", payload);
      toast.success(`Ticket ${data.numero_ticket} creato`);
      setShowNew(false); nav(`/helpdesk/ticket/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
    finally { setSaving(false); }
  };

  // Celle inline (stile Freshdesk)
  const stop = (e) => e.stopPropagation();
  const PriorityCell = ({ t }) => (
    <Select value={t.priorita_id || ""} onValueChange={(v) => patchRow(t.id, "priorita_id", v)}>
      <SelectTrigger onClick={stop} className="h-7 border-0 shadow-none px-1.5 gap-2 w-auto min-w-0 hover:bg-secondary rounded-sm focus:ring-0" data-testid={`row-priorita-${t.id}`}>
        <span className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ backgroundColor: t.priorita?.colore || "#94a3b8" }} />
        <span className="text-sm truncate max-w-[90px]">{t.priorita?.nome || "—"}</span>
      </SelectTrigger>
      <SelectContent onClick={stop}>{meta.priorities.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
    </Select>
  );
  const StatusCell = ({ t }) => (
    <Select value={t.stato_id || ""} onValueChange={(v) => patchRow(t.id, "stato_id", v)}>
      <SelectTrigger onClick={stop} className="h-7 border-0 shadow-none px-1.5 gap-2 w-auto min-w-0 hover:bg-secondary rounded-sm focus:ring-0" data-testid={`row-stato-${t.id}`}>
        <Pulse size={15} className="text-muted-foreground shrink-0" />
        <span className="text-sm truncate max-w-[130px]" style={{ color: t.stato?.colore }}>{t.stato?.nome || "—"}</span>
      </SelectTrigger>
      <SelectContent onClick={stop}>{meta.statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
    </Select>
  );

  const FSelect = ({ fld, coll, ph, extra }) => (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{ph}</Label>
      <Select value={fd[fld] || "all"} onValueChange={(v) => setFd({ ...fd, [fld]: v === "all" ? "" : v })}>
        <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid={`filter-${fld}`}><SelectValue placeholder={ph} /></SelectTrigger>
        <SelectContent><SelectItem value="all">Tutti</SelectItem>{extra}{meta[coll].map((x) => <SelectItem key={x.id} value={x.id}>{x.nome || x.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <InternalLayout>
      <div className="p-6 lg:p-10">
      <PageHeader title="Ticket" subtitle="Helpdesk — gestione richieste di assistenza"
        actions={<Button onClick={openNew} className="rounded-sm gap-2" data-testid="new-ticket-btn"><Plus size={16} /> Nuovo ticket</Button>} />

      <Card className="rounded-sm p-3 mb-4 shadow-none">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca in tutto (numero, oggetto, cliente, richiedente, tecnico, note…)" className="pl-8 rounded-sm w-72" data-testid="ticket-search" />
          </div>

          <Popover open={filterOpen} onOpenChange={(o) => (o ? openFilters() : setFilterOpen(false))}>
            <PopoverTrigger asChild>
              <Button variant={activeCount ? "default" : "outline"} size="sm" className="rounded-sm gap-1.5 relative" data-testid="filter-button">
                <Funnel size={15} weight={activeCount ? "fill" : "regular"} /> Filtro
                {activeCount > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/90 text-primary text-[11px] font-bold" data-testid="filter-active-badge">{activeCount}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[560px] max-h-[var(--radix-popover-content-available-height)] flex flex-col rounded-md p-0 overflow-hidden shadow-xl border-border" align="start" collisionPadding={16} data-testid="filter-panel">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/50 shrink-0">
                <Funnel size={16} weight="fill" className="text-primary" />
                <span className="font-heading text-sm font-semibold">Filtri ticket</span>
                {activeCount > 0 && <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold">{activeCount}</span>}
              </div>
              <div className="px-4 py-4 flex-1 min-h-0 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Stato</Label>
                  <div className="mt-1 rounded-sm border border-border bg-white p-2 space-y-0.5" data-testid="filter-stato_id">
                    {(() => {
                      const sel = fd.stato_id ? fd.stato_id.split(",") : [];
                      return meta.statuses.map((s) => {
                        const checked = sel.includes(s.id);
                        return (
                          <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer px-1 py-0.5 hover:bg-secondary rounded-sm" data-testid={`filter-stato-opt-${s.id}`}>
                            <Checkbox checked={checked} onCheckedChange={(v) => {
                              const next = v ? [...sel, s.id] : sel.filter((x) => x !== s.id);
                              setFd({ ...fd, stato_id: next.join(",") });
                            }} />
                            <span className="inline-flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.colore }} />
                              {s.nome}
                            </span>
                          </label>
                        );
                      });
                    })()}
                  </div>
                </div>
                <div className="space-y-2.5">
                  <FSelect fld="priorita_id" coll="priorities" ph="Priorità" />
                  <FSelect fld="sorgente_id" coll="sources" ph="Sorgente" />
                  <FSelect fld="assegnato_a_user_id" coll="users" ph="Assegnato a" />
                </div>
                <FSelect fld="gruppo_id" coll="groups" ph="Gruppo / Ufficio" />
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">SLA</Label>
                  <Select value={fd.sla_status || "all"} onValueChange={(v) => setFd({ ...fd, sla_status: v === "all" ? "" : v })}>
                    <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid="filter-sla_status"><SelectValue placeholder="SLA" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      <SelectItem value="scaduto">Scaduto</SelectItem>
                      <SelectItem value="in_scadenza">In scadenza</SelectItem>
                      <SelectItem value="sospeso">Sospeso</SelectItem>
                      <SelectItem value="rispettato">Rispettato</SelectItem>
                      <SelectItem value="active">In tempo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <FSelect fld="sla_policy_id" coll="slaPolicies" ph="Policy SLA" />
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Visibilità cliente</Label>
                  <Select value={fd.visibile_area_cliente || "all"} onValueChange={(v) => setFd({ ...fd, visibile_area_cliente: v === "all" ? "" : v })}>
                    <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid="filter-visibile"><SelectValue placeholder="Visibilità" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="true">Visibile in area cliente</SelectItem><SelectItem value="false">Non visibile</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Creato da</Label><Input type="date" value={fd.data_da} onChange={(e) => setFd({ ...fd, data_da: e.target.value })} className="mt-1 rounded-sm h-9" data-testid="filter-data-da" /></div>
                <div><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Creato a</Label><Input type="date" value={fd.data_a} onChange={(e) => setFd({ ...fd, data_a: e.target.value })} className="mt-1 rounded-sm h-9" data-testid="filter-data-a" /></div>
              </div>
              <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer rounded-sm border border-border bg-secondary/30 px-3 py-2 hover:bg-secondary/60 transition-colors">
                <Checkbox checked={fd.mostra_finali} onCheckedChange={(v) => setFd({ ...fd, mostra_finali: !!v })} data-testid="filter-mostra-finali" />
                Mostra anche ticket risolti / chiusi / annullati
              </label>
              </div>
              <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-secondary/50 shrink-0">
                <Button variant="ghost" size="sm" onClick={resetFilters} className="rounded-sm text-muted-foreground hover:text-foreground" data-testid="filter-reset">Reset filtri</Button>
                <Button size="sm" onClick={applyFilters} className="rounded-sm gap-1.5" data-testid="filter-apply"><Funnel size={14} weight="fill" /> Applica filtri</Button>
              </div>
            </PopoverContent>
          </Popover>

          {activeCount > 0 && <Button variant="ghost" size="sm" onClick={resetFilters} className="rounded-sm text-muted-foreground" data-testid="filter-clear-inline">Azzera</Button>}

          <div className="flex items-center rounded-sm border border-border overflow-hidden" data-testid="view-toggle">
            <button onClick={() => setViewMode("list")} className={`flex items-center gap-1.5 px-2.5 h-8 text-xs ${viewMode === "list" ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground hover:bg-secondary"}`} data-testid="view-list-btn" title="Vista elenco"><ListBullets size={15} /> Elenco</button>
            <button onClick={() => setViewMode("table")} className={`flex items-center gap-1.5 px-2.5 h-8 text-xs border-l border-border ${viewMode === "table" ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground hover:bg-secondary"}`} data-testid="view-table-btn" title="Vista tabella"><TableIco size={15} /> Tabella</button>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <button onClick={load} className="text-muted-foreground hover:text-foreground" title="Aggiorna ora" data-testid="ticket-refresh"><ArrowsClockwise size={15} /></button>
            {lastRefresh && <span className="text-[11px] text-muted-foreground" data-testid="ticket-last-refresh">Aggiornato {lastRefresh.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>}
            <span className="text-sm text-muted-foreground" data-testid="ticket-count">{total} ticket · pag. {page}/{pages || 1}</span>
            <div className="flex items-center gap-1" data-testid="ticket-pager">
              <Button variant="outline" size="sm" className="rounded-sm h-7 px-2.5" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="ticket-prev-page">‹ Prec.</Button>
              <Button variant="outline" size="sm" className="rounded-sm h-7 px-2.5" disabled={page >= (pages || 1)} onClick={() => setPage((p) => p + 1)} data-testid="ticket-next-page">Succ. ›</Button>
            </div>
          </div>
        </div>
      </Card>

      {selected.length > 0 && (
        <Card className="rounded-sm p-2.5 mb-3 shadow-none border-blue-200 bg-blue-50/60 flex flex-wrap items-center gap-2" data-testid="bulk-bar">
          <span className="text-sm font-medium text-blue-800 px-1" data-testid="bulk-count">{selected.length} selezionati</span>
          <Select onValueChange={(v) => bulkAction("set_status", v)}>
            <SelectTrigger className="w-40 rounded-sm bg-white h-8" data-testid="bulk-status"><SelectValue placeholder="Cambia stato" /></SelectTrigger>
            <SelectContent>{meta.statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={(v) => bulkAction("set_priority", v)}>
            <SelectTrigger className="w-36 rounded-sm bg-white h-8" data-testid="bulk-priority"><SelectValue placeholder="Priorità" /></SelectTrigger>
            <SelectContent>{meta.priorities.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={(v) => bulkAction("assign_user", v)}>
            <SelectTrigger className="w-44 rounded-sm bg-white h-8" data-testid="bulk-assign"><SelectValue placeholder="Assegna a" /></SelectTrigger>
            <SelectContent>{meta.users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={(v) => bulkAction("set_group", v)}>
            <SelectTrigger className="w-40 rounded-sm bg-white h-8" data-testid="bulk-group"><SelectValue placeholder="Gruppo" /></SelectTrigger>
            <SelectContent>{meta.groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.nome}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => bulkAction("delete")} className="rounded-sm h-8 gap-1 text-rose-600 border-rose-200 hover:bg-rose-50 ml-auto" data-testid="bulk-delete"><Trash size={14} /> Elimina</Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected([])} className="rounded-sm h-8" data-testid="bulk-clear">Deseleziona</Button>
        </Card>
      )}

      {items === null ? <p className="text-sm text-muted-foreground">Caricamento…</p> : viewMode === "table" ? (
        <Card className="rounded-sm shadow-none overflow-x-auto" data-testid="ticket-table-view">
          <Table style={{ tableLayout: "fixed", width: totalW }} className="[&_td]:overflow-hidden [&_th]:overflow-hidden">
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: colW.check }} className="relative"><Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-check-table" /><RH k="check" /></TableHead>
                <TableHead style={{ width: colW.sorgente }} className="relative" title="Sorgente"><RH k="sorgente" /></TableHead>
                <SortHead label="Cliente / Richiedente" k="cliente" />
                <SortHead label="Numero" k="numero" sk="numero_ticket" />
                <SortHead label="Oggetto" k="oggetto" />
                <SortHead label="Priorità" k="priorita" />
                <SortHead label="Stato" k="stato" />
                <SortHead label="Assegnato a" k="assegnatario" />
                <SortHead label="Creato" k="creato" sk="data_creazione" />
                <SortHead label="Aggiornato" k="aggiornato" sk="data_ultimo_aggiornamento" />
                <SortHead label="SLA" k="sla" />
                <TableHead style={{ width: colW.azioni }} className="relative"><RH k="azioni" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground py-16"><TicketIcon size={28} className="mx-auto mb-2 opacity-40" />Nessun ticket</TableCell></TableRow>}
              {filtered.map((t) => {
                const who = t.richiedente_nome || t.richiedente_email || t.cliente_nome || "—";
                return (
                  <TableRow key={t.id} onClick={() => nav(`/helpdesk/ticket/${t.id}`)} className="cursor-pointer hover:bg-secondary/40" data-testid={`ticket-table-row-${t.id}`}>
                    <TableCell onClick={stop}><Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggleSel(t.id)} data-testid={`row-check-table-${t.id}`} /></TableCell>
                    <TableCell className="text-muted-foreground" title={`Sorgente: ${t.sorgente?.nome || "—"}`} data-testid={`row-source-table-${t.id}`}><SourceIcon name={t.sorgente?.icon} size={16} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {t.cliente_nome ? `${t.cliente_nome} (${who})` : who}
                      {t.cliente_stato === "cessato" && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-rose-100 text-rose-700 border border-rose-300">CESSATO</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">#{t.numero_ticket}</TableCell>
                    <TableCell className="font-medium max-w-[280px] truncate">{t.oggetto}</TableCell>
                    <TableCell onClick={stop}><PriorityCell t={t} /></TableCell>
                    <TableCell onClick={stop}><StatusCell t={t} /></TableCell>
                    <TableCell onClick={stop}><AssigneeCell t={t} meta={meta} patchRow={patchRow} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap tabular-nums">{fmtDateTime(t.data_creazione)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap text-muted-foreground">{timeAgo(t.data_ultimo_aggiornamento)}</TableCell>
                    <TableCell>{t.sla_status && t.sla_status !== "none" ? <SlaBadge status={t.sla_status} testid={`row-sla-table-${t.id}`} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell onClick={stop}><Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600 hover:bg-rose-50 rounded-sm" onClick={() => setDelTarget(t)} data-testid={`row-delete-table-${t.id}`}><Trash size={15} /></Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="rounded-sm shadow-none overflow-hidden divide-y divide-border">
          {filtered.length > 0 && (
            <div className="flex items-center gap-3 px-5 py-2 bg-secondary/30 text-xs text-muted-foreground" data-testid="select-all-row">
              <Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-check" />
              <span>Seleziona tutti</span>
            </div>
          )}
          {filtered.length === 0 && (
            <div className="text-center text-muted-foreground py-16"><TicketIcon size={30} className="mx-auto mb-2 opacity-40" />Nessun ticket</div>
          )}
          {filtered.map((t) => {
            const isNew = !t.data_ultima_risposta_operatore;
            const who = t.richiedente_nome || t.richiedente_email || t.cliente_nome || "—";
            return (
              <div key={t.id} onClick={() => nav(`/helpdesk/ticket/${t.id}`)}
                className="flex items-start gap-4 px-5 py-4 cursor-pointer hover:bg-secondary/40 transition-colors" data-testid={`ticket-row-${t.id}`}>
                <div className="pt-1" onClick={stop}>
                  <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggleSel(t.id)} data-testid={`row-check-${t.id}`} />
                </div>
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-slate-700 shrink-0"
                  style={{ backgroundColor: avatarColor(who) }} aria-hidden>{initial(who)}</div>

                <div className="min-w-0 flex-1">
                  {isNew && <span className="inline-block mb-1 text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">Nuovo</span>}
                  <div className="text-[15px] font-medium text-slate-800 leading-snug">
                    <span className="hover:underline">{t.oggetto}</span>
                    <span className="text-muted-foreground font-normal ml-2">#{t.numero_ticket}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] text-muted-foreground mt-1">
                    <span className="inline-flex items-center gap-1 shrink-0" title={`Sorgente: ${t.sorgente?.nome || "—"}`} data-testid={`row-source-${t.id}`}>
                      <SourceIcon name={t.sorgente?.icon} size={14} />
                    </span>
                    <span className="text-slate-600">{t.cliente_nome ? `${t.cliente_nome} (${who})` : who}</span>
                    {t.cliente_stato === "cessato" && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-rose-100 text-rose-700 border border-rose-300" data-testid={`row-cessato-${t.id}`}>CESSATO</span>}
                    <Circle size={4} weight="fill" className="opacity-40" />
                    <span data-testid={`row-creato-${t.id}`} title={fmtDateTime(t.data_creazione)}>Creato il {fmtDateTime(t.data_creazione)}</span>
                    <Circle size={4} weight="fill" className="opacity-40" />
                    <span>Agg. {timeAgo(t.data_ultimo_aggiornamento)}</span>
                  </div>
                  {t.has_completed_linked_activity && (
                    <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-300"
                         title="Un intervento collegato è stato completato: verifica ed eventualmente chiudi il ticket"
                         data-testid={`row-completed-activity-${t.id}`}>
                      <Warning size={13} weight="fill" className="text-amber-500" />
                      {t.completed_linked_activities_count > 1
                        ? `${t.completed_linked_activities_count} interventi collegati completati — verifica ticket`
                        : "Intervento collegato completato — verifica ticket"}
                    </div>
                  )}
                  {t.sla_status && t.sla_status !== "none" && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <SlaBadge status={t.sla_status} testid={`row-sla-${t.id}`} />
                      {t.sla_policy_nome && <span className="text-[11px] text-muted-foreground">{t.sla_policy_nome}</span>}
                    </div>
                  )}
                </div>

                <div className="hidden md:flex flex-col items-start gap-1.5 shrink-0 min-w-[190px]">
                  <PriorityCell t={t} />
                  <AssigneeCell t={t} meta={meta} patchRow={patchRow} />
                  <StatusCell t={t} />
                </div>
                <div className="pt-1 shrink-0" onClick={stop}>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600 hover:bg-rose-50 rounded-sm"
                    onClick={() => setDelTarget(t)} title="Elimina ticket" data-testid={`row-delete-${t.id}`}>
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="delete-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Elimina ticket</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare il ticket <strong>#{delTarget?.numero_ticket}</strong> — "{delTarget?.oggetto}"? Il ticket verrà rimosso dalla lista.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)} className="rounded-sm" data-testid="delete-cancel-btn">Annulla</Button>
            <Button onClick={confirmDelete} disabled={deleting} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-confirm-btn">
              {deleting ? "Eliminazione…" : "Elimina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="new-ticket-dialog">
          <DialogHeader><DialogTitle>Nuovo ticket</DialogTitle><DialogDescription>Registra una richiesta. Numero e stato di default vengono applicati automaticamente; la sorgente predefinita è Telefono.</DialogDescription></DialogHeader>
          {form && <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div><Label className="text-xs">Cliente *</Label>
              <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="mt-1 w-full justify-between rounded-sm font-normal" data-testid="nt-cliente">
                    <span className="truncate">{form.cliente_id ? (meta.clients.find((c) => c.id === form.cliente_id)?.ragione_sociale || "Cliente") : (form.cliente_nome_libero ? `${form.cliente_nome_libero} (senza anagrafica)` : "Cerca o crea cliente…")}</span>
                    <MagnifyingGlass size={14} className="opacity-60 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Cerca cliente…" value={clientQuery} onValueChange={setClientQuery} data-testid="nt-cliente-search" />
                    <CommandList>
                      <CommandEmpty>
                        <div className="p-2 text-sm space-y-1">
                          <button className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-secondary text-red-600 font-medium disabled:opacity-50"
                            disabled={!clientQuery.trim()}
                            onClick={useFreeClientName}
                            data-testid="nt-cliente-libero">Usa solo il nome{clientQuery.trim() ? ` "${clientQuery.trim()}"` : ""} <span className="text-red-400">(senza anagrafica)</span></button>
                          <button className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-secondary text-primary font-medium disabled:opacity-50"
                            disabled={creatingClient || !clientQuery.trim()}
                            onClick={createClientInline}
                            data-testid="nt-cliente-create">＋ Crea nuovo cliente{clientQuery.trim() ? ` "${clientQuery.trim()}"` : ""}</button>
                        </div>
                      </CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="__none__" onSelect={() => onSelectClient("")}>— Nessun cliente —</CommandItem>
                        {meta.clients.filter((c) => !clientQuery.trim() || (c.ragione_sociale || "").toLowerCase().includes(clientQuery.toLowerCase())).slice(0, 50).map((c) => <CommandItem key={c.id} value={c.id} onSelect={() => onSelectClient(c.id)} data-testid={`nt-cliente-opt-${c.id}`}>{c.ragione_sociale}</CommandItem>)}
                        {freeNames.filter((n) => !clientQuery.trim() || n.toLowerCase().includes(clientQuery.toLowerCase())).slice(0, 20).map((n) => (
                          <CommandItem key={`free-${n}`} value={`free-${n}`} onSelect={() => selectFreeName(n)} data-testid={`nt-cliente-free-opt-${n}`}>
                            {n} <span className="text-muted-foreground ml-1 text-xs">(senza anagrafica)</span>
                          </CommandItem>
                        ))}
                        {clientQuery.trim() && !freeNames.some((n) => n.toLowerCase() === clientQuery.trim().toLowerCase()) && (
                          <CommandItem value="__libero__" onSelect={useFreeClientName} data-testid="nt-cliente-libero-item" className="text-red-600 font-medium">Usa solo il nome "{clientQuery.trim()}" <span className="text-red-400 ml-1">(senza anagrafica)</span></CommandItem>
                        )}
                        {clientQuery.trim() && (
                          <CommandItem value="__create__" onSelect={createClientInline} disabled={creatingClient} data-testid="nt-cliente-create-item" className="text-primary font-medium">＋ Crea nuovo cliente "{clientQuery.trim()}"</CommandItem>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative"><Label className="text-xs">Referente *</Label>
                <Input value={form.richiedente_nome}
                  onChange={(e) => setForm({ ...form, richiedente_nome: e.target.value })}
                  onFocus={() => setRefFocus(true)}
                  onBlur={() => setTimeout(() => setRefFocus(false), 150)}
                  placeholder={form.cliente_id ? "Nome referente (digita per suggerimenti)…" : "Nome referente"}
                  className="mt-1 rounded-sm" data-testid="nt-referente" autoComplete="off" />
                {refFocus && form.cliente_id && refSuggestions.length > 0 && (
                  <div className="absolute z-30 left-0 right-0 mt-1 max-h-52 overflow-y-auto rounded-sm border border-border bg-white shadow-lg" data-testid="nt-referente-menu">
                    {refSuggestions.map((r, i) => (
                      <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); pickReferente(r); }}
                        className="w-full text-left px-3 py-2 hover:bg-secondary text-sm" data-testid={`nt-referente-opt-${i}`}>
                        <div className="font-medium text-slate-700">{r.nome || r.email}{r.ruolo ? <span className="text-muted-foreground font-normal"> · {r.ruolo}</span> : ""}</div>
                        {(r.email || r.telefono) && <div className="text-[11px] text-muted-foreground">{[r.email, r.telefono].filter(Boolean).join(" · ")}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div><Label className="text-xs">Inserito da</Label>
                <Input value={authUser?.name || authUser?.email || ""} readOnly disabled className="mt-1 rounded-sm bg-muted/50" data-testid="nt-inserito-da" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Contatto — Email</Label><Input value={form.richiedente_email} onChange={(e) => setForm({ ...form, richiedente_email: e.target.value })} placeholder="email@cliente.it" className="mt-1 rounded-sm" data-testid="nt-email" /></div>
              <div><Label className="text-xs">Contatto — Telefono</Label><Input value={form.richiedente_telefono} onChange={(e) => setForm({ ...form, richiedente_telefono: e.target.value })} placeholder="+39…" className="mt-1 rounded-sm" data-testid="nt-telefono" /></div>
            </div>
            <div><Label className="text-xs">Oggetto richiesta *</Label><Input value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} className="mt-1 rounded-sm" data-testid="nt-oggetto" /></div>
            <div><Label className="text-xs">Richiesta *</Label><Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} rows={4} className="mt-1 rounded-sm" data-testid="nt-descrizione" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Assegnazione Gruppo *</Label>
                <Select value={form.gruppo_id || ""} onValueChange={(v) => setForm({ ...form, gruppo_id: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="nt-gruppo"><SelectValue placeholder="Seleziona gruppo…" /></SelectTrigger>
                  <SelectContent>{meta.groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Assegnazione Persona</Label>
                <Select value={form.assegnato_a_user_id || "none"} onValueChange={(v) => setForm({ ...form, assegnato_a_user_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="nt-assegnatario"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Non assegnato</SelectItem>{meta.users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Sorgente</Label>
                <Select value={form.sorgente_id || "none"} onValueChange={(v) => setForm({ ...form, sorgente_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="nt-sorgente"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{meta.sources.map((s) => <SelectItem key={s.id} value={s.id}><span className="flex items-center gap-2"><SourceIcon name={s.icon} size={14} />{s.nome}</span></SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Priorità</Label>
                <Select value={form.priorita_id || "none"} onValueChange={(v) => setForm({ ...form, priorita_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="nt-priorita"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{meta.priorities.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="rounded-sm">Annulla</Button>
            <Button onClick={create} disabled={saving} className="rounded-sm" data-testid="nt-submit">{saving ? "Creazione…" : "Crea ticket"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </InternalLayout>
  );
}
